import React, { useState, useEffect } from 'react';
import {
  Box,
  Input,
  Table,
  Typography,
  Button,
  FormControl,
  FormLabel,
  Card,
  Alert
} from '@mui/joy';
import axiosInstance from '../util/axiosConfig';
import MaterialPopup from '../components/MaterialPopup';

const SORT_OPTIONS = {
  material_name: { label: 'Material Name', param: 'material_name' },
  created_by: { label: 'Created By', param: 'created_by' },
  total_quantity: { label: 'Total Quantity', param: 'total_quantity' },
  earliest_expiry: { label: 'Earliest Expiry', param: 'earliest_expiry' }
};

const StockList = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState('material_name');
  const [sortOrder, setSortOrder] = useState('asc');
  const itemsPerPage = 10;

  useEffect(() => {
    fetchMaterials();
  }, [searchTerm, dateFrom, dateTo, sortBy, sortOrder]);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const params = {
        sortBy,
        sortOrder
      };
      if (searchTerm) params.search = searchTerm;
      if (dateFrom) params.dateFrom = dateFrom;
      if (dateTo) params.dateTo = dateTo;

      const response = await axiosInstance.get('/materials', { params });
      setMaterials(response.data.materials || []);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch materials');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
    setCurrentPage(1);
  };

  const SortableHeader = ({ column, children, style = {} }) => (
    <th
      style={{ cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => handleSort(column)}
      title={`Sort by ${SORT_OPTIONS[column]?.label || column}`}
    >
      {children}
      {sortBy === column && (
        <span style={{ marginLeft: 4, opacity: 0.9 }}>
          {sortOrder === 'asc' ? ' ▲' : ' ▼'}
        </span>
      )}
    </th>
  );

  const handleMaterialClick = (materialName) => {
    setSelectedMaterial(materialName);
  };

  const handleClosePopup = () => {
    setSelectedMaterial(null);
    fetchMaterials();
  };

  const totalPages = Math.ceil(materials.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedMaterials = materials.slice(startIndex, startIndex + itemsPerPage);

  return (
    <Box sx={{ padding: 3 }}>
      <Typography level="h1" sx={{ mb: 3, color: '#eb7a24' }}>
        Materials
      </Typography>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
          <FormControl sx={{ flex: 1, minWidth: 200 }}>
            <FormLabel>Search by Material Name</FormLabel>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search materials..."
              sx={{
                '&:hover': { borderColor: '#ea7a23' },
                '&:focus-within': { borderColor: '#ea7a23' }
              }}
            />
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <FormLabel>Purchase Date From</FormLabel>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              sx={{
                '&:hover': { borderColor: '#ea7a23' },
                '&:focus-within': { borderColor: '#ea7a23' }
              }}
            />
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <FormLabel>Purchase Date To</FormLabel>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              sx={{
                '&:hover': { borderColor: '#ea7a23' },
                '&:focus-within': { borderColor: '#ea7a23' }
              }}
            />
          </FormControl>
        </Box>
      </Card>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography level="body-md">
              Showing {startIndex + 1}-{Math.min(startIndex + itemsPerPage, materials.length)} of {materials.length} materials
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
                sx={{
                  borderColor: '#eb7a24',
                  color: '#eb7a24',
                  '&:hover': { borderColor: '#cc6a1f', backgroundColor: 'rgba(235, 122, 36, 0.1)' }
                }}
              >
                Previous
              </Button>
              <Button
                variant="outlined"
                disabled={currentPage === totalPages || totalPages === 0}
                onClick={() => setCurrentPage(currentPage + 1)}
                sx={{
                  borderColor: '#eb7a24',
                  color: '#eb7a24',
                  '&:hover': { borderColor: '#cc6a1f', backgroundColor: 'rgba(235, 122, 36, 0.1)' }
                }}
              >
                Next
              </Button>
            </Box>
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Table
              sx={{
                '& thead th': {
                  backgroundColor: '#eb7a24',
                  color: 'white',
                  fontWeight: 'bold',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                },
                '& tbody tr:nth-of-type(even)': {
                  backgroundColor: '#f9f9f9'
                },
                '& tbody tr:hover': {
                  backgroundColor: '#fff4e6',
                  cursor: 'pointer'
                },
                '& td': {
                  wordWrap: 'break-word',
                  whiteSpace: 'normal'
                }
              }}
            >
              <thead>
                <tr>
                  <SortableHeader column="material_name" style={{ width: '30%' }}>
                    Material Name
                  </SortableHeader>
                  <SortableHeader column="total_quantity" style={{ width: '25%' }}>
                    Total Quantity
                  </SortableHeader>
                  <SortableHeader column="earliest_expiry" style={{ width: '25%' }}>
                    Earliest Expiry Date
                  </SortableHeader>
                  <th style={{ width: '20%' }}>Vendor Name</th>
                  <SortableHeader column="created_by" style={{ width: '20%' }}>
                    Created By
                  </SortableHeader>
                </tr>
              </thead>
              <tbody>
                {paginatedMaterials.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'center', padding: '2rem' }}>
                      No materials found
                    </td>
                  </tr>
                ) : (
                  paginatedMaterials.map((material, index) => (
                    <tr
                      key={index}
                      onClick={() => handleMaterialClick(material.material_name)}
                    >
                      <td style={{ color: '#eb7a24', fontWeight: 'bold' }}>
                        {material.material_name}
                      </td>
                      <td>
                        {material.total_quantity}
                        {Number(material.total_quantity) === 0 && (
                          <Typography component="span" level="body-xs" sx={{ color: 'text.tertiary', ml: 0.5 }}>
                            (Out of stock)
                          </Typography>
                        )}
                      </td>
                      <td>
                        {material.earliest_expiry
                          ? new Date(material.earliest_expiry).toLocaleDateString()
                          : 'N/A'}
                      </td>
                      <td>{material.vendor_name || '—'}</td>
                      <td>{material.created_by || '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </Box>
        </>
      )}

      {selectedMaterial && (
        <MaterialPopup
          materialName={selectedMaterial}
          onClose={handleClosePopup}
        />
      )}
    </Box>
  );
};

export default StockList;

